import { livyConfig, livyFetch } from 'src/lib/livyTransport';
import { AUTH_TOKEN_KEYS } from 'src/constants/authToken';
import { preRefreshAccessToken } from 'src/core/services/api/httpClient';
import { restoreSelectedCompany } from 'src/lib/sessionCompany';
import { StreamError } from 'src/lib/livyStream';

/**
 * JSON calls over the same signed transport the chat stream uses, so no surface has to know that
 * the endpoint is an IAM-authenticated function URL rather than an ordinary API.
 */
export class LivyApiError extends Error {
  constructor(message, status) {
    super(message);
    this.status = status;
    this.name = 'LivyApiError';
  }
}

/**
 * The stored token, refreshed first when it has expired.
 *
 * Livy signs its own requests and never goes through Axios, so the app's refresh interceptor never
 * sees these calls. A session left idle past expiry therefore failed here while every other part of
 * the app silently refreshed and carried on, which reads as "Livy is broken" rather than "your
 * token aged out". The refresh itself is the app's own, so there is one implementation and one
 * in-flight guard.
 */
export async function freshToken() {
  const expiresOn = localStorage.getItem(AUTH_TOKEN_KEYS.expiresOn);
  const refreshToken = localStorage.getItem(AUTH_TOKEN_KEYS.refreshToken);
  if (expiresOn && refreshToken && Date.parse(expiresOn) <= Date.now()) {
    try {
      await preRefreshAccessToken();
    } catch (_unused) {
      // Fall through to whatever is stored: the request will answer 401 and say so plainly,
      // which is better than throwing something the caller cannot explain.
    }
  }
  return localStorage.getItem(AUTH_TOKEN_KEYS.accessToken);
}

/**
 * The token for a staff Customer preview, on the company the app has selected. When the session
 * cannot be moved onto it, the turn is refused rather than answered from another company under
 * this company's label.
 */
export async function tokenForCompany() {
  await freshToken();
  if (!(await restoreSelectedCompany())) {
    throw new StreamError("Livy couldn't switch to the selected company. Select it again in the company list, then retry.");
  }
  return localStorage.getItem(AUTH_TOKEN_KEYS.accessToken);
}
export async function livyJson(path) {
  var _init$method;
  let init = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};
  const config = livyConfig();
  if (!config) throw new LivyApiError('Livy is not configured in this environment.', 0);
  const token = await freshToken();
  if (!token) throw new LivyApiError('Your session expired. Sign in again.', 401);
  const res = await livyFetch(config, token, {
    method: (_init$method = init.method) !== null && _init$method !== void 0 ? _init$method : 'GET',
    path,
    body: init.body === undefined ? undefined : JSON.stringify(init.body)
  });
  if (!res.ok) {
    var _error;
    // The server answers {error} for every failure, so surface that rather than a bare status.
    const detail = await res.json().catch(() => ({}));
    throw new LivyApiError((_error = detail.error) !== null && _error !== void 0 ? _error : "Request failed (".concat(res.status, ")"), res.status);
  }
  return await res.json();
}